import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import type { Package } from 'interfaces/Taxonomy';
import type { HeaderNav, MenuItemProps } from 'interfaces/navigation/Header';
import type { PianoPageConfig } from 'services/Piano/entities/PianoPageConfig';
import getPackage from 'api/Package/getPackage';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import { type HeadData, setHeadData } from 'utils/metaDataUtils/headData';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import setPackagePageAdConfig from 'services/Ad/config/setPackagePageAdConfig';
import { PageAdConfig } from 'interfaces/ads/Ad';
import { setPianoConfig } from 'services/Piano';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import getMostPopularPosts from 'api/MostPopular/getMostPopular';
import { layoutCount, topicNavBorderColor } from 'constants/constants';
import HubPage from 'pages/Hub/HubPage';
import MostPopularRR from 'components/MostPopularRR';
import getFooter from 'api/Footer/getFooter';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import getMenu from 'api/Header/getMenu';
import { PageType } from 'interfaces/content/articles/Post';
import logApiError from 'utils/log/logApiError';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';

interface PackageProps {
  mostPopular: ArticlePreview[];
  packageItem: Package;
  slug: string;
  subMenu?: MenuItemProps[];
  pageSize: number;
  pagination: PagePagination;
}

const PackageNextPage: NextPage<PackageProps> = ({
  mostPopular,
  packageItem,
  slug,
  subMenu,
  pageSize,
  pagination,
}: PackageProps) => (
  <HubPage
    data={packageItem}
    type={PageType.PACKAGES}
    sidebar={[
      mostPopular && (
        <MostPopularRR
          key={1}
          articles={mostPopular}
        />
      ),
    ]}
    infiniteScroll={{ numPostsPerPage: layoutCount.packages, slug, taxonomy: 'packages' }}
    subMenu={subMenu}
    color={topicNavBorderColor.own}
    pageSize={pageSize}
    pagination={pagination}
  />
);
export default PackageNextPage;

interface Props {
  params: {
    slug: string;
  };
}

export const getStaticProps: GetStaticProps = async (context) => {
  const pageSize = layoutCount.packages;

  try {
    const { slug } = context.params as { slug: string };
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);

    const packageItem: Package = await getPackage(slug, pageSize, (currentPageNum - 1) * pageSize);
    const totalArticles = packageItem.pageInfo.totalCount > 0 ? packageItem.pageInfo.totalCount : 0;

    const pagination = calculatePagePagination(currentPageNum, totalArticles);

    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);

    const mostPopular = await getMostPopularPosts().catch((error) => {
      logApiError(error as Error, `[pages][packages][slug: ${slug}]`);
      return [];
    });

    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(packageItem), fallbackData.dataLayer);
    const headData: HeadData = safe(() => setHeadData(packageItem), fallbackData.headData);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(packageItem),
      fallbackData.arcSpanPageConfig,
    );
    const pianoPageConfig: PianoPageConfig = safe(() => setPianoConfig(packageItem), fallbackData.pianoPageConfig);
    const pageAdConfig: PageAdConfig = safe(() => setPackagePageAdConfig(packageItem), fallbackData.pageAdConfig);
    const subMenu = menu?.topicNavigation.find((menuItem) => menuItem.label === packageItem.name)?.subMenu || null;

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        headData,
        headerNav: menu,
        mostPopular,
        packageItem,
        pageAdConfig,
        pageSize,
        pagination,
        pianoPageConfig,
        slug,
        subMenu,
      },
      revalidate: 60,
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths<{ slug: string }> = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            slug: 'april-may-2022',
          },
        },
      ],
    };
  }

  const paths: Props[] = [
    {
      params: {
        slug: 'december-2023-january-2024',
      },
    },
    {
      params: {
        slug: 'february-march-2024',
      },
    },
    {
      params: {
        slug: 'april-may-2024',
      },
    },
  ];
  return {
    fallback: 'blocking',
    paths,
  };
};
